import type { AttachmentResource } from "api/generated";
import type { Attachment } from "shared/types";

type MediaFlagSource = Pick<
  AttachmentResource,
  "is_video" | "is_audio" | "transcode_status" | "stream_url"
>;

type MediaFlags = Pick<
  Attachment,
  | "is_video"
  | "is_audio"
  | "isPlayableVideo"
  | "isPendingOrFailedVideo"
  | "isPlayableAudio"
  | "isPendingOrFailedAudio"
>;

/**
 * Derives the media booleans every attachment mapper exposes. Video and audio
 * share the transcode lifecycle, so "playable" means completed AND streamable
 * and "pending or failed" is everything else for that media kind. Components
 * must reach for these flags, never recompute them from the raw fields.
 */
export const mapAttachmentMediaFlags = (
  resource: MediaFlagSource,
): MediaFlags => {
  const isVideo = resource.is_video;
  const isAudio = resource.is_audio;
  const isCompleted = resource.transcode_status === "completed";
  const hasStreamUrl = Boolean(resource.stream_url);
  const isPlayable = isCompleted && hasStreamUrl;

  return {
    is_video: isVideo,
    is_audio: isAudio,
    isPlayableVideo: isVideo && isPlayable,
    isPendingOrFailedVideo: isVideo && isPlayable === false,
    isPlayableAudio: isAudio && isPlayable,
    isPendingOrFailedAudio: isAudio && isPlayable === false,
  };
};

export const mapAttachment = (resource: AttachmentResource): Attachment => ({
  id: resource.id,
  name: resource.name,
  attachment: resource.attachment,
  url: resource.url,
  previewUrl: resource.preview_url,
  mimetype: resource.mimetype,
  transcode_status: resource.transcode_status,
  stream_url: resource.stream_url,
  thumbnail_url: resource.thumbnail_url,
  ...mapAttachmentMediaFlags(resource),
});

/**
 * Reference-stable wrapper around `mapAttachment` for legacy code paths
 * (learning-objectives) that map raw API arrays at the component level
 * rather than at fetch time. Returns the SAME mapped array for the same
 * raw input array reference so consumers (e.g. AttachmentList) don't see
 * identity-thrashing on every parent render.
 *
 * Use only when the upstream store doesn't already map on fetch.
 */
const mappedAttachmentsCache = new WeakMap<
  readonly AttachmentResource[],
  Attachment[]
>();

export const mapAttachments = (
  resources: readonly AttachmentResource[] | null | undefined,
): Attachment[] => {
  if (!resources || resources.length === 0) {
    return [];
  }

  const cached = mappedAttachmentsCache.get(resources);
  if (cached !== undefined) {
    return cached;
  }

  const mapped = resources.map(mapAttachment);
  mappedAttachmentsCache.set(resources, mapped);
  return mapped;
};
